import type { EventCheckout, OrganizationPublic } from '@irace/shared';
import { PoweredByFooter } from '../components/PoweredByFooter.tsx';
import { COPY } from '../copy/vi.ts';
import { checkoutLabels } from './checkoutResume.ts';

/**
 * G-014 — the optional check-out control (§17 extension).
 *
 * Rendered only when the bootstrap carried a `checkout` block, so an event
 * without check-out shows exactly the screen it shows today.
 *
 * Two things this panel deliberately does not do:
 *
 * - It never renders a Turnstile widget of its own. The one widget in
 *   `CheckinExperience` is reused, which is what keeps the check-out token on
 *   the same action as check-in. `POST /api/v1/checkouts` verifies both routes
 *   against one configured `TURNSTILE_ACTION`, so a second widget with a
 *   check-out-flavoured action would mint tokens Siteverify reports as
 *   `action-mismatch` — a 403 on every departure that no one at the venue can
 *   diagnose.
 * - It never shows the button again once the check-out is `done`. Tapping it
 *   would re-save the record as pending with a zero attempt count and move the
 *   recorded departure time forward, so a participant who double-tapped would
 *   have left later than they did.
 */

/**
 * `pending` and `failed` are both retryable and both keep the button, because
 * every reachable failure here recovers by trying again: a blocked IndexedDB
 * upgrade clears when the stale tab closes, a failed challenge needs a fresh
 * one, and a dead network comes back. A state with no way forward is the one
 * outcome this screen must never produce.
 */
export type CheckoutState = 'idle' | 'sending' | 'pending' | 'done' | 'failed';

const STATUS: Partial<Record<CheckoutState, string>> = {
  sending: COPY.checkout.sending,
  pending: COPY.checkout.pending,
  failed: COPY.checkout.failed,
};

export function CheckoutPanel({
  checkout,
  state,
  onCheckout,
}: {
  checkout: EventCheckout;
  state: CheckoutState;
  onCheckout: () => void;
}) {
  const labels = checkoutLabels(checkout);

  if (state === 'done') {
    return (
      <div
        role="status"
        aria-live="polite"
        className="flex flex-col items-center gap-2 border-t border-[#e4e9f0] pt-6"
      >
        <p className="text-base font-bold text-success">{COPY.checkout.done}</p>
      </div>
    );
  }

  const status = STATUS[state];

  return (
    <div className="flex w-full flex-col items-center gap-3 border-t border-[#e4e9f0] pt-6">
      <p className="text-sm text-text-muted">{labels.notice}</p>

      <button
        type="button"
        onClick={onCheckout}
        disabled={state === 'sending'}
        className="h-13 w-full rounded-control bg-brand px-6 text-[17px] font-extrabold tracking-wide text-brand-contrast disabled:opacity-60"
      >
        {state === 'sending' ? COPY.checkout.sending : labels.button}
      </button>

      {/* Amber for both, never green: `pending` means the phone has it and the
          server does not, and `failed` means not even that (invariant 5). */}
      {status ? (
        <p role="status" aria-live="polite" className="text-sm font-semibold text-warning">
          {status}
        </p>
      ) : null}

      <p className="text-xs leading-relaxed text-text-muted">{labels.keepOpenHint}</p>
    </div>
  );
}

/**
 * The screen a participant sees when they reopen the page after checking in.
 *
 * It exists because the check-out button lives on the accepted screen, and
 * that screen is gone the moment the phone is locked and the tab is reloaded.
 * Without this, "mở lại và bấm check-out" would land them on an empty form.
 *
 * `checkedInAt` is already formatted in the event's timezone by the caller,
 * for the same reason §28.3 formats the opening time there: a participant with
 * a wrong device clock or a phone still on another timezone must read back the
 * organizer's time, not their own.
 */
export function CheckoutResumeScreen({
  checkedInAt,
  locationName,
  checkout,
  state,
  onCheckout,
  onCheckinAgain,
  organization,
}: {
  checkedInAt: string | null;
  /** Null when the event has no real location, so none was ever shown. */
  locationName: string | null;
  checkout: EventCheckout;
  state: CheckoutState;
  onCheckout: () => void;
  onCheckinAgain: () => void;
  organization?: OrganizationPublic | null;
}) {
  return (
    <div className="mx-auto flex min-h-dvh w-full max-w-[480px] flex-col justify-between bg-bg px-5 py-10">
      <div className="flex flex-1 flex-col items-center justify-center gap-4 text-center">
        <div
          className="flex size-16 items-center justify-center rounded-full bg-success text-3xl text-white"
          aria-hidden="true"
        >
          ✓
        </div>

        <h1 className="text-xl font-extrabold tracking-wide text-success">
          {COPY.checkout.resumeHeading}
        </h1>

        {checkedInAt ? (
          <p className="text-base text-text-muted">
            {COPY.checkout.resumeAt} {checkedInAt}
          </p>
        ) : null}

        {locationName ? (
          <div className="flex flex-col gap-1">
            <span className="text-xs font-semibold tracking-wide text-text-muted uppercase">
              {COPY.accepted.locationLabel}
            </span>
            <span className="text-base font-bold">{locationName}</span>
          </div>
        ) : null}

        <CheckoutPanel checkout={checkout} state={state} onCheckout={onCheckout} />
      </div>

      <div className="flex flex-col gap-3">
        {/*
          The way back to the form, and it is not optional: accepted records
          live for six hours (§17), so without this a phone that checked one
          person in could not check in a second — the resume screen would hold
          the whole device for the rest of the event. Outlined rather than
          filled, unlike the same control on the accepted screen: check-out is
          the reason this screen exists, and its button has to stay the loudest
          thing on it.
        */}
        <button
          type="button"
          onClick={onCheckinAgain}
          className="h-12 w-full rounded-control border-[1.5px] border-brand px-5 text-base font-bold text-brand"
        >
          {COPY.accepted.checkinAgain}
        </button>

        <PoweredByFooter organization={organization} />
      </div>
    </div>
  );
}
